// Reads an image's pixel size from its first bytes, without decoding it (FR-3).
// Sizes are "as displayed": JPG EXIF orientation and HEIC rotation are applied.
import type { Size } from "@/lib/image/size";

/** Enough bytes for every format here, even JPGs with a big EXIF block. */
export const HEADER_BYTES = 256 * 1024;

const ascii = (b: Uint8Array, at: number, len: number) =>
  String.fromCharCode(...b.subarray(at, at + len));

export function readImageSize(bytes: Uint8Array): Size | null {
  if (bytes.length < 12) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  try {
    if (bytes[0] === 0x89 && ascii(bytes, 1, 3) === "PNG") {
      return { width: view.getUint32(16), height: view.getUint32(20) };
    }
    if (ascii(bytes, 0, 3) === "GIF") {
      return {
        width: view.getUint16(6, true),
        height: view.getUint16(8, true),
      };
    }
    if (ascii(bytes, 0, 2) === "BM") {
      return {
        width: Math.abs(view.getInt32(18, true)),
        height: Math.abs(view.getInt32(22, true)),
      };
    }
    if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP") {
      return readWebp(bytes, view);
    }
    if (bytes[0] === 0xff && bytes[1] === 0xd8) return readJpeg(bytes, view);
    if (ascii(bytes, 4, 4) === "ftyp") return readHeif(bytes, view);
  } catch {
    // Truncated or unusual file: the worker will find the size by decoding.
  }
  return null;
}

function readWebp(bytes: Uint8Array, view: DataView): Size | null {
  const chunk = ascii(bytes, 12, 4);
  if (chunk === "VP8 ") {
    return {
      width: view.getUint16(26, true) & 0x3fff,
      height: view.getUint16(28, true) & 0x3fff,
    };
  }
  if (chunk === "VP8L") {
    const bits = view.getUint32(21, true);
    return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 };
  }
  if (chunk === "VP8X") {
    const u24 = (at: number) =>
      bytes[at] | (bytes[at + 1] << 8) | (bytes[at + 2] << 16);
    return { width: u24(24) + 1, height: u24(27) + 1 };
  }
  return null;
}

function readJpeg(bytes: Uint8Array, view: DataView): Size | null {
  let orientation = 1;
  let at = 2;
  while (at + 9 < bytes.length) {
    if (bytes[at] !== 0xff) return null;
    const marker = bytes[at + 1];
    if (marker === 0xff) {
      at++; // padding
      continue;
    }
    const length = view.getUint16(at + 2);
    if (marker === 0xe1 && ascii(bytes, at + 4, 4) === "Exif") {
      orientation = exifOrientation(view, at + 10) ?? orientation;
    }
    const isFrame =
      marker >= 0xc0 &&
      marker <= 0xcf &&
      marker !== 0xc4 &&
      marker !== 0xc8 &&
      marker !== 0xcc;
    if (isFrame) {
      const size = {
        width: view.getUint16(at + 7),
        height: view.getUint16(at + 5),
      };
      // Orientations 5–8 turn the photo on its side.
      return orientation >= 5
        ? { width: size.height, height: size.width }
        : size;
    }
    at += 2 + length;
  }
  return null;
}

function exifOrientation(view: DataView, tiff: number): number | null {
  const little = view.getUint16(tiff) === 0x4949; // "II"
  const ifd = tiff + view.getUint32(tiff + 4, little);
  const count = view.getUint16(ifd, little);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (view.getUint16(entry, little) === 0x0112) {
      return view.getUint16(entry + 8, little);
    }
  }
  return null;
}

/** HEIC/AVIF: the largest "ispe" box is the main picture; "irot" turns it. */
function readHeif(bytes: Uint8Array, view: DataView): Size | null {
  let best: Size | null = null;
  let quarterTurns = 0;
  for (let at = 4; at + 16 < bytes.length; at++) {
    if (bytes[at] !== 0x69 && bytes[at] !== 0x73) continue; // 'i' (ispe/irot)
    const box = ascii(bytes, at, 4);
    if (box === "ispe") {
      const size = {
        width: view.getUint32(at + 8),
        height: view.getUint32(at + 12),
      };
      if (!best || size.width * size.height > best.width * best.height) {
        best = size;
      }
    } else if (box === "irot") {
      quarterTurns = bytes[at + 4] & 0x3;
    }
  }
  if (!best) return null;
  return quarterTurns % 2 === 1
    ? { width: best.height, height: best.width }
    : best;
}

/** A JPG's EXIF orientation (1 = upright), or 1 for anything else. */
export function jpegOrientation(bytes: Uint8Array): number {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return 1;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let at = 2;
  try {
    while (at + 10 < bytes.length && bytes[at] === 0xff) {
      const marker = bytes[at + 1];
      if (marker === 0xda) break; // image data starts: no EXIF after this
      const length = view.getUint16(at + 2);
      if (marker === 0xe1 && ascii(bytes, at + 4, 4) === "Exif") {
        return exifOrientation(view, at + 10) ?? 1;
      }
      at += 2 + length;
    }
  } catch {
    // Unusual file: treat as upright; the worker still decodes it safely.
  }
  return 1;
}
